'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Icon } from '@/components/ui/icon';
import { IconButton } from '@/components/ui/icon-button';
import styles from './theme-toggle.module.css';

type Theme = 'light' | 'dark';

function resolveTheme(): Theme {
  const stored = localStorage.getItem('theme');
  if (stored === 'light' || stored === 'dark') return stored;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

interface Props {
  /** 'icon' = square icon button (header); 'row' = full-width row with label (mobile nav) */
  variant?: 'icon' | 'row';
}

export function ThemeToggle({ variant = 'icon' }: Props) {
  const t = useTranslations('nav');
  const [theme, setTheme] = useState<Theme | null>(null);

  useEffect(() => {
    // SSR-safe theme hydration: server renders with theme=null (icon hidden
    // via aria-hidden + the CSS @media fallback handles tokens). On first
    // client tick we resolve from localStorage / OS. useSyncExternalStore
    // is the principled alternative but would need a server snapshot of
    // `null` and a custom storage-event subscriber to handle same-origin
    // writes; the cascade here is the simpler correct path.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setTheme(resolveTheme());

    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent) => {
      if (localStorage.getItem('theme')) return; // user has explicit preference
      setTheme(e.matches ? 'dark' : 'light');
    };
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  function toggle() {
    if (theme === null) return; // pre-hydration, ignore clicks
    const next: Theme = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('theme', next);
    document.documentElement.setAttribute('data-theme', next);
  }

  const isDark = theme === 'dark';
  const label = isDark ? t('switchToLight') : t('switchToDark');

  if (variant === 'row') {
    return (
      <button
        type="button"
        className={styles.rowButton}
        onClick={toggle}
        aria-label={label}
        // Reserve space before hydration to avoid layout shift
        aria-hidden={theme === null ? 'true' : undefined}
      >
        {isDark
          ? <Icon name="sun" size={18} strokeWidth={1.5} aria-hidden />
          : <Icon name="moon" size={18} strokeWidth={1.5} aria-hidden />}
        <span>{isDark ? t('lightMode') : t('darkMode')}</span>
      </button>
    );
  }

  return (
    <IconButton
      icon={isDark ? 'sun' : 'moon'}
      label={label}
      onClick={toggle}
      aria-hidden={theme === null ? 'true' : undefined}
    />
  );
}
